import type { FlowPageType } from "@repo/shared";
import {
  allAdjustmentQuestionsAnswered,
  computeAdjustment,
  enabledQuestions as filterEnabledQuestions,
  renderedRiskSections,
  resolveRiskInputScopes,
  riskSectionsAnswered,
} from "@repo/shared";
import type { MissingRequirementReason } from "@repo/shared/types";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { getByIdPublic } from "@/lib/api/risk-models";
import { usePublicSession } from "@/lib/context/public-session-context";
import { useRiskModel } from "@/lib/hooks/use-risk-models";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import type { ComponentStyleType } from "@/lib/types/flow";
import { getScoreRange } from "@/lib/utils/risk-scoring";
import { InfoNote } from "../info-note";
import { MisplacedComponentNotice } from "../misplaced-component-notice";
import type { ActingRepresentative } from "../representative-note";
import { AdjustmentQuestionsSection } from "./adjustment-questions-section";
import { ResultBlock } from "./result-block";
import { T } from "./risk-assessment-widget.i18n";
import { RiskOptionSection } from "./risk-option-section";

type RiskAssessmentValue = {
  /**
   * QL-212 — optional, because a stored blob carries only the inputs the row it
   * lives in owns: a goal's row under a mixed model holds the time horizon and
   * nothing else. A value read back through the service is the MERGED one, so
   * both are present whenever the client has answered them anywhere.
   */
  timeHorizonId?: string;
  riskLevelId?: string;
  /**
   * Present only on values READ BACK from the server (its calculated result).
   * The widget never emits a score: the matrix cell is resolved server-side,
   * so a browser-supplied score would be an unverifiable second source.
   */
  score?: number;
  /** QP-180 — pure matrix score before adjustment/cap (present when they differ). */
  scoreRaw?: number;
  /** QP-180 — the client's answers to the model's adjustment questions. */
  adjustmentAnswers?: Record<string, string>;
};

/**
 * Canonical serialization of the widget's captured state, used for the
 * "did anything change?" comparison in `lastSentValueRef`. Answer keys are
 * sorted so the string is stable regardless of insertion order.
 *
 * All three inputs count equally: nothing captured — no time horizon, no risk
 * level AND no adjustment answer — is `null`, so an empty form has exactly one
 * representation. A page that captures only the adjustment questions therefore
 * serializes its answers like any other selection.
 */
function serializeSelection(
  timeHorizonId: string,
  riskLevelId: string,
  answers: Record<string, string>,
): string | null {
  if (!timeHorizonId && !riskLevelId && Object.keys(answers).length === 0) return null;
  const sorted: Record<string, string> = {};
  for (const key of Object.keys(answers).sort()) sorted[key] = answers[key]!;
  return JSON.stringify({ timeHorizonId, riskLevelId, adjustmentAnswers: sorted });
}

type RiskAssessmentWidgetProps = {
  riskModelId: string;
  /**
   * QL-212 — the type of page this instance of the widget sits on. Together with
   * the risk model's three scopes it decides which of the four sections render
   * here: a global page shows what the client answers once, a goal page shows
   * what is answered per goal, and the result block follows the score.
   *
   * The widget derives this itself from the model it already loads, so no caller
   * has to know the scopes to place it correctly.
   */
  pageType: FlowPageType;
  /**
   * The goal this assessment belongs to, or null on a global page. When it
   * changes (multi-goal tab switch), the widget re-seeds its selections from the
   * new goal's `value` instead of keeping the previous goal's — mirrors
   * `GoalInputWidget`.
   */
  goalId: string | null;
  /**
   * The stored assessment this widget seeds from — the MERGED one (QL-212 D4),
   * so a section this page does not render still contributes its value to the
   * score readout. What the widget emits is filtered back down to the sections
   * this page owns.
   */
  value?: string | null;
  /**
   * `isComplete` says whether every INPUT section this page renders has been
   * answered, which is what the caller uses to choose submit over partial-save.
   * The widget decides it because only it knows which sections it rendered —
   * under a mixed model "complete" on a goal page can mean a time horizon alone.
   */
  onChange: (value: string, isComplete: boolean) => void;
  disabled?: boolean;
  // Configurable styling props
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  hasValidationError?: boolean;
  /** QL-188 — group answering note shown under the header; null for non-group clients. */
  groupNote?: string | null;
  /**
   * QL-212 — the completion engine's reason for an open global slot. Only
   * `risk_not_scored` is rendered specially: the answer is recorded but no goal
   * could be scored from it, which "complete the risk assessment" would
   * misdescribe — the client has nothing left to answer.
   */
  missingReason?: MissingRequirementReason;
  // ── QP-180 — config-driven score adjustment ──
  /** Persisted FINAL effective score for this goal (goal.riskScore). */
  finalScore?: number | null;
  /** True when the persisted final score was clamped to the model cap. */
  isScoreCapped?: boolean;
  /** QP-184 — Art. 54(6): K&E-related adjustment answers are the representative's. */
  actingRepresentative?: ActingRepresentative | null;
};

export function RiskAssessmentWidget({
  riskModelId,
  pageType,
  goalId,
  value,
  onChange,
  disabled = false,
  style = "border",
  title,
  description,
  hasValidationError = false,
  groupNote = null,
  missingReason,
  finalScore = null,
  isScoreCapped = false,
  actingRepresentative,
}: RiskAssessmentWidgetProps) {
  // Public pages have no advisor session — read the risk model via the public API.
  const { isPublic } = usePublicSession();
  const { userLanguage } = useUserLanguage();
  const t = useT(T);

  const [selectedTimeHorizon, setSelectedTimeHorizon] = useState<string>("");
  const [selectedRiskLevel, setSelectedRiskLevel] = useState<string>("");
  const [calculatedScore, setCalculatedScore] = useState<number | null>(null);
  // QP-180 — the client's answers to the config-driven adjustment questions.
  const [adjustmentAnswers, setAdjustmentAnswers] = useState<Record<string, string>>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const onChangeRef = useRef(onChange);
  // Tracks the last value sent to onChange — prevents re-submitting on re-render/remount
  const lastSentValueRef = useRef<string | null>(null);

  // Keep the ref updated
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Advisor flow: use React Query (deduplicates in StrictMode, caches on tab switch)
  const { model: advisorRiskModel, isLoading: advisorLoading } = useRiskModel(
    !isPublic ? riskModelId : null,
  );
  // Public flow: inline query with public API
  const { data: publicRiskModel, isLoading: publicLoading } = useQuery({
    queryKey: ["publicRiskModel", riskModelId],
    queryFn: () => getByIdPublic(riskModelId!),
    enabled: isPublic && !!riskModelId,
    staleTime: Infinity,
  });

  const riskModel = isPublic ? (publicRiskModel ?? null) : advisorRiskModel;
  const loading = isPublic ? publicLoading : advisorLoading;

  // Seed the selections from a snapshot value (or clear them when there is none).
  // Shared by the initial mount and the per-goal reset below so both stay in sync.
  // QP-180 — the widget's internal score is always the RAW matrix score
  // (`scoreRaw ?? score`): the persisted `score` is the final effective value,
  // which may include an advisor adjustment/cap the matrix lookup knows nothing about.
  const seedFromValue = useCallback((val: string | null | undefined) => {
    if (val) {
      try {
        const parsed: RiskAssessmentValue = JSON.parse(val);
        // Server-calculated values only; a freshly emitted selection carries none.
        const rawScore = parsed.scoreRaw ?? parsed.score ?? null;
        const answers = parsed.adjustmentAnswers ?? {};
        // QL-212 — a stored blob legitimately omits an input another row owns.
        const timeHorizonId = parsed.timeHorizonId ?? "";
        const riskLevelId = parsed.riskLevelId ?? "";
        setSelectedTimeHorizon(timeHorizonId);
        setSelectedRiskLevel(riskLevelId);
        setCalculatedScore(rawScore);
        setAdjustmentAnswers(answers);
        // Record what the save effect will compute, so it can skip the no-op fire
        lastSentValueRef.current = serializeSelection(timeHorizonId, riskLevelId, answers);
      } catch (err) {
        log.error({ err }, "failed to parse risk assessment value");
      }
    } else {
      setSelectedTimeHorizon("");
      setSelectedRiskLevel("");
      setCalculatedScore(null);
      setAdjustmentAnswers({});
      lastSentValueRef.current = null;
    }
  }, []);

  // Parse the initial value once the risk model is loaded.
  useEffect(() => {
    if (!riskModel || isInitialized) return; // Wait for the model; seed only once.
    seedFromValue(value);
    setIsInitialized(true);
  }, [value, isInitialized, riskModel, seedFromValue]);

  // Re-seed when switching goals (multi-goal). The widget instance is reused
  // across goal tabs, so without this it would keep the previous goal's
  // selections. Mirrors `GoalInputWidget`'s `useEffect([goalId])` reset.
  // On a global page `goalId` is null and never changes, so this never fires —
  // the client-level answer is the same on every tab, which is the point.
  const prevGoalIdRef = useRef(goalId);
  useEffect(() => {
    if (prevGoalIdRef.current === goalId) return;
    prevGoalIdRef.current = goalId;
    seedFromValue(value);
  }, [goalId, value, seedFromValue]);

  // Adopt EXTERNAL updates to this assessment (e.g. an AI tool sets the score) when
  // `value` changes for the SAME goalId — the init/[goalId] effects above don't cover
  // that, so the refetched value would otherwise be ignored until a remount. Guard:
  // only adopt when the form has no unsaved local edits (the current selections still
  // match the last-persisted value), so we never clobber the user mid-selection.
  useEffect(() => {
    if (!isInitialized || !riskModel) return;

    let incoming: string | null = null;
    if (value) {
      try {
        const parsed: RiskAssessmentValue = JSON.parse(value);
        incoming = serializeSelection(
          parsed.timeHorizonId ?? "",
          parsed.riskLevelId ?? "",
          parsed.adjustmentAnswers ?? {},
        );
      } catch {
        return;
      }
    }
    // (a) did the server value actually change vs what we last applied?
    if (incoming === lastSentValueRef.current) return;

    // (b) is the form clean (local selections still equal the last-applied value)?
    // Adjustment answers count as much as the two selections — on a page that
    // captures only the questions they are the whole local state.
    const localSerialized = serializeSelection(
      selectedTimeHorizon,
      selectedRiskLevel,
      adjustmentAnswers,
    );
    if (localSerialized !== lastSentValueRef.current) return; // user mid-edit — leave it

    seedFromValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // QL-212 — which of the four sections belong on THIS page, for THIS model.
  // Derived from the model the widget already fetched, so the caller only has to
  // say which kind of page it is.
  const sections = useMemo(
    () => renderedRiskSections(resolveRiskInputScopes(riskModel), pageType),
    [riskModel, pageType],
  );

  // Save partial progress OR complete score when selections change
  useEffect(() => {
    if (!isInitialized || !riskModel || disabled) {
      return;
    }

    // QL-212 — the state below is the EFFECTIVE assessment: the selections this
    // page renders, plus the ones seeded from the row that owns them. The matrix
    // readout therefore works on a goal page that only asks for a time horizon.
    const bothKnown = Boolean(selectedTimeHorizon && selectedRiskLevel);
    const matrixScore = bothKnown
      ? riskModel.matrixScores.find(
          (ms: { timeHorizonId: string; riskLevelId: string }) =>
            ms.timeHorizonId === selectedTimeHorizon && ms.riskLevelId === selectedRiskLevel,
        )
      : undefined;
    // Shown immediately as optimistic feedback only. The score the flow actually
    // stores is derived server-side from this same matrix cell — it is
    // deliberately NOT part of the payload below.
    setCalculatedScore(matrixScore ? matrixScore.score : null);

    // A pair the model has no cell for is left alone, exactly as before: the
    // configuration gap surfaces through flow completion, not as a failed save.
    if (bothKnown && !matrixScore) return;

    // Nothing answered on THIS page yet — there is no half-finished answer to
    // save. (Before QL-212 this was "neither selection made".)
    const pageHasAnAnswer =
      (sections.has("timeHorizon") && !!selectedTimeHorizon) ||
      (sections.has("riskLevel") && !!selectedRiskLevel) ||
      (sections.has("adjustmentQuestions") && Object.keys(adjustmentAnswers).length > 0);
    if (!pageHasAnAnswer) return;

    // Emit only the inputs this page owns (D4): a globally-scoped answer belongs
    // to the instance-level row and must not be written back into a goal's.
    const owned: RiskAssessmentValue = {
      ...(sections.has("timeHorizon") && { timeHorizonId: selectedTimeHorizon }),
      ...(sections.has("riskLevel") && { riskLevelId: selectedRiskLevel }),
      ...(sections.has("adjustmentQuestions") && { adjustmentAnswers }),
    };
    // Complete = every input section THIS page renders has an answer. Under the
    // all-per-goal default that is both selections, exactly as before.
    const isComplete = riskSectionsAnswered(
      sections,
      owned,
      filterEnabledQuestions(riskModel.adjustmentQuestions),
    );
    const emitted: RiskAssessmentValue = {
      ...owned,
      ...(isComplete ? {} : { score: 0 }), // 0 indicates an incomplete assessment
    };
    // Compare on the canonical serialization of the whole effective state
    // (stable answer-key order) so an unchanged selection never re-fires
    // onChange on remount, and an externally-adopted value never echoes back.
    const canonical = serializeSelection(selectedTimeHorizon, selectedRiskLevel, adjustmentAnswers);
    if (canonical === lastSentValueRef.current) return;
    lastSentValueRef.current = canonical;
    onChangeRef.current(JSON.stringify(emitted), isComplete);
  }, [
    selectedTimeHorizon,
    selectedRiskLevel,
    adjustmentAnswers,
    riskModel,
    isInitialized,
    disabled,
    sections,
  ]);

  const scoreRange = useMemo(
    () => getScoreRange(riskModel?.matrixScores),
    [riskModel?.matrixScores],
  );

  // Get container class based on style
  const getContainerClass = () => {
    if (hasValidationError) {
      return "border border-destructive rounded-lg p-6 space-y-6";
    }
    switch (style) {
      case "border":
        return "border rounded-lg p-6 space-y-6";
      case "box":
        return "rounded-lg p-6 space-y-6 bg-muted/50";
      default:
        return "space-y-6";
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">{t.loadingRiskAssessment}</div>;
  }

  if (!riskModel) {
    return <div className="text-sm text-destructive">{t.riskModelNotFound}</div>;
  }

  // QL-212 (D6) — this placement renders none of the four sections: the model
  // captures every input on the other page type. New placements like this are
  // refused when the flow is saved, and flow completion drops the ones that
  // predate the guard (or were stranded by a risk-model swap) — so all that is
  // left is to tell the advisor why the page looks empty, with the same notice
  // any misplaced component gets. The client-facing flow shows nothing at all,
  // as it does for every misplaced component.
  if (sections.size === 0) {
    return isPublic ? null : (
      <MisplacedComponentNotice
        componentType="risk_assessment"
        pageType={pageType}
        language={userLanguage}
      />
    );
  }

  // QP-180 — "cards" renders rich selectable cards; "dropdown" keeps the
  // legacy radio lists byte-for-byte. Purely visual — capture is identical.
  const isCardsMode = riskModel.displayType === "cards";

  // QP-180 — config-driven adjustment questions (both advisor + public). Plain
  // computation (not a hook) — this runs after the `!riskModel` early return.
  const enabledQuestions = filterEnabledQuestions(riskModel.adjustmentQuestions);
  const hasQuestions = enabledQuestions.length > 0;

  // The selections this page is responsible for. The questions and the result
  // block only appear once the page's OWN selections are made — on an all-goal
  // or all-global page that is both of them, exactly as before QL-212.
  const pageSelectionsMade =
    (!sections.has("timeHorizon") || !!selectedTimeHorizon) &&
    (!sections.has("riskLevel") || !!selectedRiskLevel);

  // Score readout: Initial · Adjustment · Final. The adjustment is derived via
  // the shared QL-8 engine from the client's answers at the INITIAL score; only
  // its truncated `applied` value is shown/added — the flow never renders the
  // decimal sum. The server persists the authoritative value (and the cap).
  const liveAdjustment =
    calculatedScore === null
      ? 0
      : computeAdjustment(enabledQuestions, adjustmentAnswers, calculatedScore).applied;
  const questionsComplete = allAdjustmentQuestionsAnswered(enabledQuestions, adjustmentAnswers);
  const clampedFinal =
    calculatedScore === null
      ? null
      : Math.min(Math.max(calculatedScore + liveAdjustment, scoreRange.min), scoreRange.max);
  // Prefer the server's persisted final score once all questions are answered
  // (it carries the cap); otherwise show the live client-computed value.
  const displayedFinalScore =
    calculatedScore === null
      ? null
      : questionsComplete && finalScore != null
        ? finalScore
        : clampedFinal;
  const showBreakdown = calculatedScore !== null && (hasQuestions || liveAdjustment !== 0);

  const setAnswer = (questionId: string, answerId: string) => {
    setAdjustmentAnswers((prev) => ({ ...prev, [questionId]: answerId }));
  };

  return (
    <div className={getContainerClass()}>
      {/* Optional Header */}
      <FlowComponentHeader title={title} description={description} />
      {groupNote && <InfoNote text={groupNote} />}
      {hasValidationError && (
        <p className="text-sm text-destructive">
          {missingReason === "risk_not_scored" ? t.notScoredMessage : t.incompleteMessage}
        </p>
      )}

      {sections.has("timeHorizon") && (
        <RiskOptionSection
          heading={t.timeHorizon}
          options={riskModel.timeHorizons}
          selected={selectedTimeHorizon}
          onSelect={setSelectedTimeHorizon}
          idPrefix="horizon"
          isCardsMode={isCardsMode}
          disabled={disabled}
          language={userLanguage}
        />
      )}

      {sections.has("riskLevel") && (
        <RiskOptionSection
          heading={t.riskTolerance}
          options={riskModel.riskLevels}
          selected={selectedRiskLevel}
          onSelect={setSelectedRiskLevel}
          idPrefix="level"
          isCardsMode={isCardsMode}
          disabled={disabled}
          language={userLanguage}
        />
      )}

      {sections.has("adjustmentQuestions") && pageSelectionsMade && hasQuestions && (
        <AdjustmentQuestionsSection
          questions={enabledQuestions}
          answers={adjustmentAnswers}
          onAnswer={setAnswer}
          disabled={disabled}
          language={userLanguage}
          actingRepresentative={actingRepresentative}
        />
      )}

      {sections.has("result") && pageSelectionsMade && (
        <ResultBlock
          initialScore={calculatedScore}
          adjustment={liveAdjustment}
          finalScore={displayedFinalScore}
          showBreakdown={showBreakdown}
          showCapWarning={isScoreCapped && questionsComplete}
        />
      )}
    </div>
  );
}
